import { Link } from "react-router-dom";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/hooks/useStore";
import { toast } from "sonner";

interface Lobby {
  id: string;
  name: string;
  host_id: string;
  is_public: boolean;
  status: string;
}

export function Playtest() {
  const { user } = useAuth();
  const { state } = useStore();
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [name, setName] = useState("");

  async function load() {
    const { data } = await supabase
      .from("lobbies")
      .select("id, name, host_id, is_public, status")
      .eq("is_public", true)
      .eq("status", "waiting")
      .order("name");
    setLobbies((data ?? []) as Lobby[]);
  }

  useEffect(() => { load(); }, []);

  async function createLobby() {
    if (!user) {
      toast.error("Sign in to host a lobby");
      return;
    }
    const lobbyName = name.trim() || "Quick Lobby";
    const lobbyKey = Math.random().toString(36).slice(2, 8).toUpperCase();
    const { data, error } = await supabase.from("lobbies").insert({
      name: lobbyName,
      host_id: user.id,
      is_public: true,
      lobby_key: lobbyKey,
      status: "waiting",
    }).select().single();
    if (error) { toast.error(error.message); return; }
    window.location.href = `/playtest/lobby/${data.id}`;
  }

  return (
    <div className="space-y-4">
      <PageTitle>Playtest</PageTitle>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel className="space-y-3">
          <h2 className="font-display text-base font-semibold">Solo</h2>
          <p className="text-sm text-[#64748b]">
            Play both sides locally. Best for testing card interactions.
          </p>
          <Link to="/playtest/solo"><Button>Start solo session</Button></Link>
          {state.decks.length === 0 && <p className="text-xs text-amber-soft">Tip: build a deck first.</p>}
        </Panel>

        <Panel className="space-y-3">
          <h2 className="font-display text-base font-semibold">Pass and Play</h2>
          <p className="text-sm text-[#64748b]">
            Two players, one device. Hands stay hidden — the screen passes
            back and forth on every priority change.
          </p>
          <Link to="/playtest/pass-and-play"><Button>Start pass-and-play</Button></Link>
        </Panel>

        <Panel className="space-y-3">
          <h2 className="font-display text-base font-semibold">Online lobby</h2>
          <p className="text-sm text-[#64748b]">Host or join a public lobby.</p>
          <div className="flex gap-2">
            <input className="gm-input flex-1" placeholder="Lobby name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
            <Button onClick={createLobby}>Host</Button>
          </div>

          <div className="mt-3 space-y-1">
            <p className="text-xs uppercase tracking-wider text-[#64748b]">Open lobbies</p>
            {lobbies.length === 0 ? (
              <p className="text-xs text-[#64748b]">No public lobbies right now.</p>
            ) : (
              lobbies.map((l) => (
                <Link key={l.id} to={`/playtest/lobby/${l.id}`} className="block rounded border border-[#2a2f3a] bg-[#0f1115] px-2 py-1 text-sm hover:border-amber/50">
                  {l.name} <span className="text-xs text-[#64748b]">({l.status})</span>
                </Link>
              ))
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
